import React, { Component } from 'react';
import { ThemeContext } from '../../../context/theme.context';
import '../style.css';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Legend,
  Category,
  StackingColumnSeries,
  ColumnSeries,
  Tooltip,
  DataLabel,
  ILoadedEventArgs,
  LineSeries,
} from '@syncfusion/ej2-react-charts';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { getTradePersonGraph } from '../../../services/KPIsServices/KPIsServices';
import { Scheduler } from '../../../components/GlobalScheduler';
import { BiRefresh, BiArrowBack, BiInfoCircle } from 'react-icons/bi';
import Loader from 'react-spinners/PulseLoader';

interface IProps {}

type ViewMode = 'normal' | 'shift';

interface RawRow {
  person_code: string;
  group_code: string;
  type: 'Shift' | 'Exception';
  shift_code: string;
  color: string;
  Y: number;
}

interface SeriesTicket {
  y: string;
  value: string;
  color: string;
  type: 'Shift' | 'Exception';
}

interface PersonRow {
  person_code: string;
  [seriesKey: string]: any;
}

interface BreakdownRow {
  group_code: string;
  Y: number;
  color: string;
}

interface IState {
  showSpinner: boolean;
  isRefreshing: boolean;
  viewMode: ViewMode;
  personData: PersonRow[];
  hiddenPersons: string[];
  drillPerson: string | null;
}

const SHIFT_PALETTE = ['#22d3ee', '#666666', '#7bc96f', '#c96fd6', '#ff6f6f', '#6fd6c9'];

export default class TradepersonCapacityGraph extends Component<IProps, IState> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  private tickets: SeriesTicket[] = [];
  private rawData: RawRow[] = [];
  private chartObj: ChartComponent;
  private tradeColorMap: Map<string, string> = new Map();

  constructor(props: IProps) {
    super(props);
    this.state = {
      showSpinner: true,
      isRefreshing: false,
      viewMode: 'normal',
      personData: [],
      hiddenPersons: [],
      drillPerson: null,
    };
    this.chartObj = new ChartComponent(props);
  }

  async componentDidMount() {
    await this.getPersonGraph(Scheduler.AllFilterDAta);
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (this.context.isDataChangeKpiTradePerson) {
      this.context.setDataChangeKpiTradePerson(false);
      this.getPersonGraph(Scheduler.AllFilterDAta);
      return;
    }
    if (prevState.viewMode !== this.state.viewMode) {
      this.rebuildFromCache();
    }
  }

  onLoad(args: ILoadedEventArgs): void {}

  private mapRow = (el: any): RawRow => ({
    person_code: el.name || el.element_alias || 'UNKNOWN',
    group_code: !el.trade_code || el.trade_code === '' ? 'NO TRADE' : el.trade_code,
    type: el.type,
    shift_code: el.shift_code || '',
    color: el.type === 'Exception' ? '#789395' : el.shift_color ?? el.color,
    Y: el?.Y ? Math.abs(el.Y / 60) : 0,
  });

  async getPersonGraph(query: string, isManualRefresh: boolean = false) {
    if (isManualRefresh) {
      this.setState({ isRefreshing: true });
    } else {
      this.setState({ showSpinner: true });
    }
    await getTradePersonGraph(query)
      .then((res) => {
        this.rawData = (res?.data?.dataG3 ?? []).map((el: any) => this.mapRow(el));
        this.rebuildFromCache();
        this.setState({ showSpinner: false, isRefreshing: false, drillPerson: null });
      })
      .catch((e) => {
        console.log(e);
        this.setState({ showSpinner: false, isRefreshing: false });
      });
  }

  private buildTradeColorMap(raw: RawRow[]): Map<string, string> {
    const names = Array.from(
      new Set(raw.filter((r) => r.type !== 'Exception').map((r) => r.group_code))
    ).sort();
    const map = new Map<string, string>();
    names.forEach((name, i) => {
      const hue = (i * 137.508) % 360;
      map.set(name, `hsl(${hue}, 70%, 50%)`);
    });
    return map;
  }

  private rebuildFromCache() {
    const raw = this.rawData;
    this.tradeColorMap = this.buildTradeColorMap(raw);
    if (this.state.viewMode === 'normal') {
      this.tickets = this.buildTickets(raw);
      const personData = this.buildPersonData(raw, this.tickets);
      this.setState({ personData });
    } else {
      const { tickets, personData } = this.buildShiftView(raw);
      this.tickets = tickets;
      this.setState({ personData });
    }
  }

  private buildTickets(raw: RawRow[]): SeriesTicket[] {
    const tickets: SeriesTicket[] = [];
    let counter = 0;
    raw.forEach((row) => {
      const key = row.type === 'Exception' ? 'Exception' : row.group_code;
      const exists = tickets.find((t) => t.value === key && t.type === row.type);
      if (!exists) {
        const color =
          row.type !== 'Exception' ? this.tradeColorMap.get(row.group_code) ?? '#999999' : row.color;
        tickets.push({ y: `y${counter}`, value: key, color, type: row.type });
        counter++;
      }
    });
    return tickets;
  }

private buildPersonData(raw: RawRow[], tickets: SeriesTicket[]): PersonRow[] {
    const rows: PersonRow[] = [];
    raw.forEach((row) => {
      const key = row.type === 'Exception' ? 'Exception' : row.group_code;
      const ticket = tickets.find((t) => t.value === key && t.type === row.type);
      if (!ticket) return;
      let existing = rows.find((r) => r.person_code === row.person_code);
      if (!existing) {
        existing = { person_code: row.person_code };
        rows.push(existing);
      }
      existing[ticket.y] = ((existing[ticket.y] ?? 0) as number) + row.Y;
    });

    rows.forEach((row) => {
      let total = 0;
      tickets.forEach((t) => {
        if (t.type !== 'Exception') total += (row[t.y] as number) || 0;
      });
      row.total = Math.round(total * 10) / 10;
    });

    return rows;
  }

  private buildShiftView(raw: RawRow[]): { tickets: SeriesTicket[]; personData: PersonRow[] } {
    const shiftCodes = Array.from(
      new Set(raw.filter((r) => r.type === 'Shift' && r.shift_code).map((r) => r.shift_code))
    ).sort();

    const tickets: SeriesTicket[] = shiftCodes.map((code, i) => ({
      y: `s${i}`,
      value: code,
      color: SHIFT_PALETTE[i % SHIFT_PALETTE.length],
      type: 'Shift',
    }));

    const exceptionTicket: SeriesTicket = {
      y: 'yException',
      value: 'Exception',
      color: '#789395',
      type: 'Exception',
    };
    const hasException = raw.some((r) => r.type === 'Exception');
    if (hasException) tickets.push(exceptionTicket);

    const rows: PersonRow[] = [];
    raw.forEach((row) => {
      let existing = rows.find((r) => r.person_code === row.person_code);
      if (!existing) {
        existing = { person_code: row.person_code };
        rows.push(existing);
      }
      if (row.type === 'Exception') {
        existing[exceptionTicket.y] = ((existing[exceptionTicket.y] ?? 0) as number) + row.Y;
        return;
      }
      const ticket = tickets.find((t) => t.value === row.shift_code && t.type === 'Shift');
      if (!ticket) return;
      existing[ticket.y] = ((existing[ticket.y] ?? 0) as number) + row.Y;
    });

        rows.forEach((row) => {
      let total = 0;
      tickets.forEach((t) => {
        if (t.type !== 'Exception') total += (row[t.y] as number) || 0;
      });
      row.total = Math.round(total * 10) / 10;
    });

    return { tickets, personData: rows };
  }

  private togglePerson = (personCode: string) => {
    this.setState((prev) => {
      const hidden = prev.hiddenPersons.includes(personCode)
        ? prev.hiddenPersons.filter((t) => t !== personCode)
        : [...prev.hiddenPersons, personCode];
      return { hiddenPersons: hidden };
    });
  };

  private getVisiblePersonData(): PersonRow[] {
    return this.state.personData.filter((row) => !this.state.hiddenPersons.includes(row.person_code));
  }

  private getPersonCodes(): string[] {
    return this.state.personData.map((r) => r.person_code);
  }

  private onPersonBarClick = (args: any) => {
    const person = args?.point?.x;
    if (person) {
      this.setState({ drillPerson: person });
    }
  };

  private getBreakdownForPerson(personCode: string): BreakdownRow[] {
    const rows: BreakdownRow[] = [];
    this.rawData
      .filter((r) => r.person_code === personCode && r.type === 'Shift')
      .forEach((r) => {
        let existing = rows.find((x) => x.group_code === r.group_code);
        if (!existing) {
          const color = this.tradeColorMap.get(r.group_code) ?? '#999999';
          existing = { group_code: r.group_code, Y: 0, color };
          rows.push(existing);
        }
        existing.Y += r.Y;
      });
    return rows;
  }

  private switchViewMode = (mode: ViewMode) => {
    if (mode === this.state.viewMode) return;
    this.setState({ viewMode: mode, drillPerson: null }, () => this.rebuildFromCache());
  };

  private toggleBtnStyle(active: boolean, isDark: boolean): React.CSSProperties {
    return {
      padding: '4px 12px',
      fontSize: '13px',
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: active ? '#22d3ee' : isDark ? '#2c3a48' : '#e2e2e2',
      color: active ? '#2c3a48' : isDark ? '#EAEAEA' : '#15202B',
      transition: 'background-color 0.3s ease',
    };
  }

  render() {
    const { showSpinner, isRefreshing, drillPerson, viewMode } = this.state;
    const isDark = this.context.isDarkMode;

    if (showSpinner) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '300px' }}>
          <Loader color='#22d3ee' />
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '12px',
          borderRadius: '10px',
          backgroundColor: isDark ? '#1c2732' : '#f5f5f5',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => this.getPersonGraph(Scheduler.AllFilterDAta, true)}
              disabled={isRefreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '2px 8px',
                paddingTop: '4px',
                borderRadius: '6px',
                paddingRight: '12px',
                border: 'none',
                cursor: isRefreshing ? 'default' : 'pointer',
                backgroundColor: isDark ? '#2c3a48' : '#e2e2e2',
                color: isDark ? '#EAEAEA' : '#15202B',
                fontSize: '14px',
                fontWeight: 500,
                opacity: isRefreshing ? 0.6 : 1,
              }}
            >
              <BiRefresh size={24} style={{ paddingBottom: '2px', marginRight: '0px' }} className={isRefreshing ? 'spin-refresh' : ''} />
              <span style={{ paddingTop: '1px' }}>Refresh</span>
            </button>

            <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden' }}>
              <button style={this.toggleBtnStyle(viewMode === 'normal', isDark)} onClick={() => this.switchViewMode('normal')}>
                Default
              </button>
              <button style={this.toggleBtnStyle(viewMode === 'shift', isDark)} onClick={() => this.switchViewMode('shift')}>
                Shift (DS/NS)
              </button>
            </div>
          </div>

          {!drillPerson ? (
            <div style={{ color: '#ababab' }}>
              <BiInfoCircle style={{ paddingBottom: '2px' }} />
              <span style={{ fontSize: 12, marginLeft: '4px' }}>Click a tradesperson to view its trades</span>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: '16px', minWidth: 0 }}>
          <div
            style={{
              minWidth: '200px',
              maxWidth: '200px',
              maxHeight: '262px',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: isDark ? '#15202B' : '#ffffff',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Select TradesPerson</span>
            {this.getPersonCodes().map((person) => (
              <div key={person} style={{ display: 'flex', alignItems: 'center', margin: '6px 0' }}>
                <CheckBoxComponent checked={!this.state.hiddenPersons.includes(person)} change={() => this.togglePerson(person)} />
                <span style={{ marginLeft: '6px', fontSize: '12px' }}>{person}</span>
              </div>
            ))}
          </div>

          <div style={{ flex: '1 1 0', minWidth: 0, overflowX: 'auto', marginTop: '-20px', overflowY: 'hidden' }}>
            {drillPerson ? (
              <>
                <div
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginBottom: '6px', paddingTop: '20px', fontSize: '13px' }}
                  onClick={() => this.setState({ drillPerson: null })}
                >
                  <BiArrowBack size={16} />
                  <span style={{ marginLeft: '4px' }}>Return to Tradespeople · {drillPerson}</span>
                </div>
                <ChartComponent
                  id='personDrillChart'
                  primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Rotate45', majorGridLines: { width: 0 } }}
                  primaryYAxis={{ labelFormat: '{value}', majorGridLines: { width: 0.5 } }}
                  tooltip={{ enable: true }}
                  width='100%'
                  height='234px'
                  chartArea={{ border: { width: 0 } }}
                  background='transparent'
                >
                  <Inject services={[ColumnSeries, Category, Tooltip, DataLabel]} />
                  <SeriesCollectionDirective>
                    <SeriesDirective
                      dataSource={this.getBreakdownForPerson(drillPerson)}
                      xName='group_code'
                      yName='Y'
                      type='Column'
                      name={drillPerson}
                    />
                  </SeriesCollectionDirective>
                </ChartComponent>
              </>
            ) : (
              <ChartComponent
                id='personMainChart'
                key={viewMode}
                ref={(el) => {
                  this.chartObj = el as ChartComponent;
                }}
                primaryXAxis={{ valueType: 'Category', labelIntersectAction: 'Rotate45', majorGridLines: { width: 0 } }}
                primaryYAxis={{ labelFormat: '{value}', majorGridLines: { width: 0.5 } }}
                tooltip={{ enable: true }}
                pointClick={this.onPersonBarClick}
                loaded={this.onLoad.bind(this)}
                width='100%'
                height='282px'
                chartArea={{ border: { width: 0 } }}
                background='transparent'
                legendSettings={{
                  visible: true,
                  position: 'Top',
                  textStyle: { color: isDark ? '#EAEAEA' : '#15202B', size: '11px' },
                }}
              >
                <Inject services={[StackingColumnSeries, ColumnSeries, LineSeries, Category, Legend, Tooltip, DataLabel]} />
                <SeriesCollectionDirective>
                  {this.tickets.map((ticket) =>
                    ticket.type === 'Exception' ? (
                      <SeriesDirective
                        key={`${viewMode}-${ticket.value}`}
                        dataSource={this.getVisiblePersonData()}
                        xName='person_code'
                        yName={ticket.y}
                        name={ticket.value}
                        type='Line'
                        fill={ticket.color}
                        marker={{ visible: true, width: 8, height: 8 }}
                        zOrder={1000000}
                      />
                    ) : (
                      <SeriesDirective
                        key={`${viewMode}-${ticket.value}`}
                        dataSource={this.getVisiblePersonData()}
                        xName='person_code'
                        yName={ticket.y}
                        name={ticket.value}
                        type={viewMode === 'shift' ? 'Column' : 'StackingColumn'}
                        fill={ticket.color}
                        marker={
                          viewMode === 'shift'
                            ? {
                                visible: false,
                                dataLabel: {
                                  visible: true,
                                  position: 'Outer',
                                  font: {
                                    size: '11px',
                                    fontWeight: '600',
                                    color: isDark ? '#EAEAEA' : '#15202B',
                                  },
                                },
                              }
                            : undefined
                        }
                      />
                    )
                  )}
                  {viewMode === 'normal' && this.tickets.length > 0 && (
                  <SeriesDirective
                    key={`${viewMode}-total`}
                    dataSource={this.getVisiblePersonData()}
                    xName='person_code'
                    yName='total'
                    name='Total'
                    type='Line'
                    width={0}
                    fill='#22d3ee'
                    legendShape='Circle'
                    marker={{
                      visible: false,
                      dataLabel: {
                        visible: true,
                        position: 'Top',
                        font: { size: '12px', fontWeight: '600', color: isDark ? '#EAEAEA' :              '#15202B' },
                      },
                    }}
                    zOrder={1000001}
                  />
                )}
                </SeriesCollectionDirective>
              </ChartComponent>
            )}
          </div>
        </div>
      </div>
    );
  }
}